import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency } from '../utils/formatters';
import { LineChart } from 'react-native-chart-kit';

const StepUpSIPCalculatorScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    monthlySIP: '10000',
    stepUpPercent: '10',
    annualReturn: '12',
    years: '20',
    lumpsum: '0',
  });

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const calculateStepUpSIP = () => {
    const baseSIP = parseFloat(formData.monthlySIP);
    const stepPct = parseFloat(formData.stepUpPercent) / 100;
    const annualRate = parseFloat(formData.annualReturn) / 100;
    const years = parseFloat(formData.years);
    const lumpsum = parseFloat(formData.lumpsum);
    if ([baseSIP, stepPct, annualRate, years, lumpsum].some(v => isNaN(v) || v < 0)) { setResults(null); return; }

    const r = annualRate / 12; // monthly rate
    const months = Math.round(years * 12);
    let balance = 0;
    let invested = lumpsum;
    const yearlySeries = [];
    // Add lumpsum growth first
    balance = lumpsum * Math.pow(1 + r, months);

    for (let m = 0; m < months; m++) {
      const yearIndex = Math.floor(m / 12);
      const sipAmount = baseSIP * Math.pow(1 + stepPct, yearIndex);
      invested += sipAmount;
      // Compound then add contribution (end-of-month deposit)
      balance = balance * (1 + r) + sipAmount;
      // At year end, record series
      if ((m + 1) % 12 === 0) {
        yearlySeries.push(balance);
      }
    }

    const expectedReturns = balance - invested;
    setResults({
      investedAmount: Math.round(invested),
      expectedReturns: Math.round(expectedReturns),
      totalValue: Math.round(balance),
      yearlySeries: yearlySeries.map(v=>Math.round(v)),
      years,
    });
  };

  useEffect(() => { calculateStepUpSIP(); }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>Step-Up SIP Calculator</Text>
        <Text style={styles.heroSubtitle}>Project corpus with annual increase in SIP amount</Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1, width: '100%' }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Monthly SIP</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput style={styles.input} value={formData.monthlySIP} onChangeText={(v)=>handleInputChange('monthlySIP', v)} keyboardType="numeric" placeholder="10000" />
            </View>
          </View>
          <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Step-Up Percentage</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.stepUpPercent} onChangeText={(v)=>handleInputChange('stepUpPercent', v)} keyboardType="numeric" placeholder="10" />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
              <Text style={styles.helperText}>Increase in SIP amount every year</Text>
            </View>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Expected Return (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.annualReturn} onChangeText={(v)=>handleInputChange('annualReturn', v)} keyboardType="numeric" placeholder="12" />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
            </View>
          </View>
          <View style={[styles.inputRow, isMobile && { flexDirection: 'column' }]}>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Investment Tenure</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.years} onChangeText={(v)=>handleInputChange('years', v)} keyboardType="numeric" placeholder="20" />
                <Text style={styles.suffixText}>Years</Text>
              </View>
            </View>
            <View style={[styles.inputGroup, styles.half]}>
              <Text style={styles.label}>Lumpsum (optional)</Text>
              <View style={styles.inputContainer}>
                <Text style={styles.currencySymbol}>₹</Text>
                <TextInput style={styles.input} value={formData.lumpsum} onChangeText={(v)=>handleInputChange('lumpsum', v)} keyboardType="numeric" placeholder="0" />
              </View>
            </View>
          </View>
        </View>
        <View style={[styles.summaryPanel, isMobile && { flex: 1, width: '100%', marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Investment Summary</Text>
            <Text style={styles.panelSubtitle}>Step-Up SIP Results</Text>
          </View>
          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Expected Returns</Text><Text style={styles.summaryValue}>{formatCurrency(results.expectedReturns)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Invested Amount</Text><Text style={styles.summaryValue}>{formatCurrency(results.investedAmount)}</Text></View>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Total Value</Text><Text style={styles.totalValue}>{formatCurrency(results.totalValue)}</Text></View>
              {renderChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderChart = () => {
    if (!results?.yearlySeries?.length) return null;
    const labels = results.yearlySeries.map((_, i) => {
      const y = i + 1; return (y === 1 || y === results.years) ? String(y) : '';
    });
    const chartWidth = isMobile ? Math.min(screenData.width - 40, 320) : 320;
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Yearly Corpus Projection</Text>
        <LineChart
          data={{ labels, datasets: [{ data: results.yearlySeries }] }}
          width={chartWidth}
          height={180}
          yAxisLabel="₹"
          chartConfig={{ backgroundColor: '#ffffff', backgroundGradientFrom: '#ffffff', backgroundGradientTo: '#ffffff', decimalPlaces: 0, color: ()=>'#16A34A', labelColor: ()=>'#0f172a' }}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  };

  const renderInfo = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>What is Step-Up SIP?</Text>
      <Text style={styles.sectionText}>Step-Up SIP increases your monthly investment annually by a fixed percentage. This helps boost long-term corpus while keeping contributions affordable in early years.</Text>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={20} color="#1f2937" />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How does a Step-Up SIP grow my corpus?</Text>
            <Text style={styles.faqAnswer}>Your contribution increases every year by a chosen percentage. Combined with market returns, this compounding of both investments and contributions accelerates corpus growth.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What step-up rate should I choose?</Text>
            <Text style={styles.faqAnswer}>Many investors align step-ups to annual salary hikes (5%–15%). Pick a sustainable rate that you can continue for the full tenure.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I step up monthly instead of yearly?</Text>
            <Text style={styles.faqAnswer}>This calculator models yearly step-ups for simplicity. Monthly step-ups also work but are operationally heavier and offer marginally different results.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Does the step-up apply to the lumpsum?</Text>
            <Text style={styles.faqAnswer}>No. Lumpsum grows with market returns only. Step-ups apply to the monthly SIP amount.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What return assumption should I use?</Text>
            <Text style={styles.faqAnswer}>For diversified equity funds, long-term expectations often range 10%–12% p.a. Use conservative numbers to avoid overestimating outcomes.</Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEO = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Step-Up SIP: Boost Long-Term Wealth</Text>
      <Text style={styles.sectionText}>A Step-Up SIP increases your monthly investment every year, letting your contribution grow alongside your income. This strategy can significantly increase your final corpus compared to a flat SIP, because both invested amount and returns compound over time.</Text>
      <Text style={styles.sectionText}>Methodology: We model end-of-month contributions, yearly step-ups on the SIP amount, and monthly compounding of returns. Lumpsum—if any—is compounded throughout the tenure. Results are projections for planning and can vary.</Text>

      <Text style={styles.seoSubtitle}>Tips to optimize results</Text>
      <Text style={styles.sectionText}>• Keep step-up sustainable (5%–15%) • Review yearly • Prefer long tenures • Avoid frequent pauses • Use realistic return assumptions • Combine with occasional lumpsum additions</Text>

      <Text style={styles.seoSubtitle}>Related calculators</Text>
      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
          <Text style={styles.linkText}>SIP Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('MutualFundReturnsCalculator')}>
          <Text style={styles.linkText}>Mutual Fund Returns</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('RetirementCalculator')}>
          <Text style={styles.linkText}>Retirement Calculator</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.disclaimer}>Investments are subject to market risks. Use conservative inputs for planning.</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      {renderCalculator()}
      {renderInfo()}
      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: { backgroundColor: '#16A34A', paddingVertical: 24, paddingHorizontal: 30 },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 28, fontWeight: 'bold', color: '#ffffff', marginBottom: 6 },
  heroSubtitle: { fontSize: 14, color: '#dcfce7' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 20 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { gap: 20, flex: 0.7 },
  summaryPanel: { flex: 0.3, backgroundColor: '#ffffff', borderRadius: 12, padding: 20, height: 'fit-content', borderWidth: 1, borderColor: '#e5e7eb' },
  panelHeader: { alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b' },
  inputGroup: { marginBottom: 16 },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  helperText: { fontSize: 12, color: '#64748b', marginTop: 6 },
  inputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 48 },
  currencySymbol: { fontSize: 16, color: '#64748b', marginRight: 8 },
  percentSymbol: { fontSize: 16, color: '#64748b', marginLeft: 8 },
  suffixText: { fontSize: 14, color: '#64748b', marginLeft: 8 },
  input: { flex: 1, fontSize: 16, color: '#0f172a', borderWidth: 0 },
  resultsContainer: { gap: 12 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  placeholderText: { fontSize: 14, color: '#64748b' },
  chartSection: { marginTop: 12, alignItems: 'center' },
  chartTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  infoSection: { marginVertical: 20, marginHorizontal:20, backgroundColor: '#ffffff', padding: 20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 12 },
  sectionText: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 12 },
  faqContainer: { marginBottom: 20, marginHorizontal:20},
  faqHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: '#16A34A10', borderRadius: 12 },
  faqTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  faqContent: { backgroundColor: '#ffffff', padding: 20, borderRadius: 12, marginTop: 12, borderWidth: 1, borderColor: '#e5e7eb' },
  faqItem: { marginBottom: 16 },
  faqQuestion: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  faqAnswer: { fontSize: 15, color: '#475569', lineHeight: 22 },
  seoContainer: { backgroundColor: '#ffffff', padding: 20, borderRadius: 12, marginHorizontal:20, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, marginBottom: 20 },
  seoTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a', marginBottom: 12, marginHorizontal:20, textAlign: 'center' },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  linkButton: { paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#16A34A10', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  linkText: { color: '#16A34A', fontWeight: '600' },
  disclaimer: { fontSize: 12, color: '#64748b', marginTop: 4 },
  seoSubtitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 8, marginBottom: 6 },
});

export default StepUpSIPCalculatorScreen;